/**
 * Deal Comparison Screen
 *
 * Side-by-side comparison of startups for investors.
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack, useRouter, useLocalSearchParams } from 'expo-router';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import {
  ChevronLeft,
  X,
  TrendingUp,
  MapPin,
  Calendar,
  Users,
  Target,
  FolderOpen,
  Star,
  Minus,
  Check,
} from 'lucide-react-native';
import { getScoreColor } from '@/lib/colors';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import { DealflowStartup } from '@/lib/state/inverio-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { formatCurrency } from '@/lib/utils/format';
import { calculateMatchScore, STAGE_LABELS } from '@/lib/state/marketplace';
import { useRaisingStartups } from '@/lib/supabase/investor-queries';
import { dataRoomOpen } from '@/lib/dealflow/stage';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';

// Breadcrumbs for navigation
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Dealflow', href: '/(investor)/dealflow' },
  { label: 'Compare Deals' },
];

// ============================================
// Comparison Row
// ============================================

interface ComparisonRowProps {
  label: string;
  values: (string | number | boolean | undefined)[];
  highlight?: 'highest' | 'lowest' | 'boolean' | 'none';
  formatValue?: (value: any) => string;
}

function ComparisonRow({
  label,
  values,
  highlight = 'none',
  formatValue = (v) => String(v ?? '-'),
}: ComparisonRowProps) {
  const colors = useThemeColors();
  // Find best value for highlighting
  const numericValues = values.map(v => (typeof v === 'number' ? v : null));
  const maxValue = Math.max(...numericValues.filter((v): v is number => v !== null));
  const minValue = Math.min(...numericValues.filter((v): v is number => v !== null));

  return (
    <View
      className="flex-row border-b py-3"
      style={{ borderColor: colors.border.light }}
    >
      {/* Label */}
      <View className="w-28 pr-2">
        <Text style={{ color: colors.text.secondary }} className="text-sm">
          {label}
        </Text>
      </View>

      {/* Values */}
      {values.map((value, index) => {
        let isHighlighted = false;
        let highlightColor = colors.text.primary;

        if (highlight === 'highest' && value === maxValue) {
          isHighlighted = true;
          highlightColor = colors.success;
        } else if (highlight === 'lowest' && value === minValue) {
          isHighlighted = true;
          highlightColor = colors.success;
        } else if (highlight === 'boolean' && value === true) {
          isHighlighted = true;
          highlightColor = colors.success;
        }

        return (
          <View key={index} className="flex-1 px-2 items-center">
            {highlight === 'boolean' ? (
              value ? (
                <Check size={18} color={colors.success} />
              ) : (
                <Minus size={18} color={colors.text.tertiary} />
              )
            ) : (
              <Text
                style={{ color: isHighlighted ? highlightColor : colors.text.primary }}
                className={`text-sm text-center ${isHighlighted ? 'font-semibold' : ''}`}
              >
                {formatValue(value)}
              </Text>
            )}
          </View>
        );
      })}
    </View>
  );
}

// ============================================
// Score Comparison Row
// ============================================

interface ScoreRowProps {
  label: string;
  scores: number[];
  /**
   * Per-column "this deal has no score" flags, parallel to `scores`.
   *
   * Needed because 0 is a legitimate value here AND the placeholder for
   * absence; without the flag an unscored deal would be coloured red and
   * compared against real scores. Optional so the Match Score row (always
   * computed) is unaffected.
   */
  unscored?: boolean[];
}

function ScoreRow({ label, scores, unscored }: ScoreRowProps) {
  const colors = useThemeColors();
  // "Best" is decided among SCORED columns only.
  const scoredValues = scores.filter((_, i) => !unscored?.[i]);
  const maxScore = scoredValues.length > 0 ? Math.max(...scoredValues) : 0;

  return (
    <View
      className="flex-row border-b py-3"
      style={{ borderColor: colors.border.light }}
    >
      <View className="w-28 pr-2">
        <Text style={{ color: colors.text.secondary }} className="text-sm">
          {label}
        </Text>
      </View>

      {scores.map((score, index) => {
        const isUnscored = unscored?.[index] === true;
        const color = isUnscored ? colors.text.tertiary : getScoreColor(score);
        const isBest = !isUnscored && score === maxScore && maxScore > 0;

        return (
          <View key={index} className="flex-1 px-2 items-center">
            <View className="flex-row items-center gap-1">
              <Text
                style={{ color }}
                className={`text-lg ${isBest ? 'font-bold' : 'font-semibold'}`}
              >
                {isUnscored ? '—' : score}
              </Text>
              {isBest && <Star size={14} color={colors.warning} fill={colors.warning} />}
            </View>
          </View>
        );
      })}
    </View>
  );
}

// ============================================
// Main Screen
// ============================================

export default function DealComparisonScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const { ids } = useLocalSearchParams<{ ids: string }>();
  const { isDesktop } = useResponsive();

  const dealflow = useDealflowStore((s) => s.dealflow);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  // Raising-feed items can be compared too — they live in the feed query,
  // not the store (their id is the startup_profiles id).
  const { data: raisingFeed } = useRaisingStartups();

  // Parse IDs from query params
  const comparisonIds = ids ? ids.split(',') : [];

  // Get deals to compare
  const deals = useMemo(() => {
    return comparisonIds
      .map(
        (id) =>
          dealflow.find((d) => d.id === id) ??
          raisingFeed?.find((d) => d.id === id || d.startup.id === id),
      )
      .filter((d): d is DealflowStartup => d !== undefined);
  }, [comparisonIds, dealflow, raisingFeed]);

  // Calculate match scores
  const matchScores = useMemo(() => {
    if (!investorProfile) return deals.map(() => null);
    return deals.map(deal =>
      calculateMatchScore(deal.startup, investorProfile, deal.readinessScore ?? undefined)
    );
  }, [deals, investorProfile]);

  if (deals.length === 0) {
    const emptyContent = (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
        <Text style={{ color: colors.text.secondary }}>No deals selected for comparison</Text>
        <AnimatedPressable
          onPress={() => router.back()}
          className="mt-4 px-6 py-3 rounded-xl"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Text className="text-white font-medium">Go Back</Text>
        </AnimatedPressable>
      </View>
    );

    if (isWeb) {
      return (
        <AppLayout title="Compare Deals" breadcrumbs={BREADCRUMBS}>
          {emptyContent}
        </AppLayout>
      );
    }

    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        {emptyContent}
      </>
    );
  }

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
        {/* Header - Mobile only */}
        {phoneShell && (
          <Animated.View
            entering={FadeInDown.duration(400)}
            className="px-5 py-4 flex-row items-center justify-between"
            style={{
              backgroundColor: colors.surface.primary,
              borderBottomWidth: 1,
              borderBottomColor: colors.border.light,
            }}
          >
            <View className="flex-row items-center gap-3">
              <AnimatedPressable
                onPress={() => router.back()}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </AnimatedPressable>
              <View>
                <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                  Compare Deals
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-sm">
                  {deals.length} startups
                </Text>
              </View>
            </View>
          </Animated.View>
        )}

        <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Startup Headers */}
            <Animated.View
              entering={FadeIn.delay(100).duration(300)}
              className="flex-row px-5 py-4 border-b"
              style={{
                borderColor: colors.border.light,
                backgroundColor: colors.surface.primary,
              }}
            >
              <View className="w-28" />
              {deals.map((deal, index) => (
                <View key={deal.id} className="flex-1 px-2 items-center">
                  <View
                    className="w-14 h-14 rounded-xl items-center justify-center mb-2"
                    style={{ backgroundColor: colors.primary[100] }}
                  >
                    <Text style={{ color: colors.primary[700] }} className="text-lg font-bold">
                      {deal.startup.company.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </Text>
                  </View>
                  <Text
                    style={{ color: colors.text.primary }}
                    className="font-semibold text-center text-sm"
                    numberOfLines={2}
                  >
                    {deal.startup.company.name}
                  </Text>
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-xs text-center mt-1"
                    numberOfLines={1}
                  >
                    {deal.startup.company.industry}
                  </Text>
                </View>
              ))}
            </Animated.View>

            {/* Scores Section */}
            <Animated.View
              entering={FadeIn.delay(200).duration(300)}
              className="mx-5 mt-4 rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <View
                className="px-4 py-3 border-b"
                style={{ borderColor: colors.border.light }}
              >
                <Text style={{ color: colors.text.primary }} className="font-semibold">
                  Scores & Match
                </Text>
              </View>
              <View className="px-4">
                <ScoreRow
                  label="Readiness"
                  scores={deals.map(d => d.readinessScore ?? 0)}
                  unscored={deals.map(d => d.readinessScore == null)}
                />
                <ScoreRow
                  label="Match Score"
                  scores={matchScores.map(m => m?.overall || 0)}
                />
              </View>
            </Animated.View>

            {/* Basic Info Section */}
            <Animated.View
              entering={FadeIn.delay(300).duration(300)}
              className="mx-5 mt-4 rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <View
                className="px-4 py-3 border-b"
                style={{ borderColor: colors.border.light }}
              >
                <Text style={{ color: colors.text.primary }} className="font-semibold">
                  Basic Information
                </Text>
              </View>
              <View className="px-4">
                <ComparisonRow
                  label="Stage"
                  values={deals.map(d => STAGE_LABELS[d.startup.company.stage] || d.startup.company.stage)}
                />
                <ComparisonRow
                  label="Location"
                  values={deals.map(d => (d.startup.company.location ?? '').split(',')[0])}
                />
                <ComparisonRow
                  label="Founded"
                  values={deals.map(d => d.startup.company.founded)}
                />
                <ComparisonRow
                  label="Team Size"
                  values={deals.map(d => d.startup.team.size)}
                  highlight="highest"
                />
              </View>
            </Animated.View>

            {/* Funding Section */}
            <Animated.View
              entering={FadeIn.delay(400).duration(300)}
              className="mx-5 mt-4 rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <View
                className="px-4 py-3 border-b"
                style={{ borderColor: colors.border.light }}
              >
                <Text style={{ color: colors.text.primary }} className="font-semibold">
                  Funding
                </Text>
              </View>
              <View className="px-4">
                <ComparisonRow
                  label="Target"
                  values={deals.map(d => d.startup.funding.currentRaise?.amount ?? 0)}
                  formatValue={(v) => formatCurrency(v)}
                />
                <ComparisonRow
                  label="Data Room"
                  values={deals.map(d => dataRoomOpen(d.status))}
                  highlight="boolean"
                />
              </View>
            </Animated.View>

            {/* Traction Section */}
            <Animated.View
              entering={FadeIn.delay(500).duration(300)}
              className="mx-5 mt-4 rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <View
                className="px-4 py-3 border-b"
                style={{ borderColor: colors.border.light }}
              >
                <Text style={{ color: colors.text.primary }} className="font-semibold">
                  Traction
                </Text>
              </View>
              <View className="px-4">
                <ComparisonRow
                  label="MRR"
                  values={deals.map(d => d.startup.traction.metrics.find(m => m.name === 'MRR')?.value ?? 0)}
                  formatValue={(v) => v ? formatCurrency(v) : '-'}
                  highlight="highest"
                />
                <ComparisonRow
                  label="Users"
                  values={deals.map(d => d.startup.traction.metrics.find(m => m.name === 'Users')?.value ?? 0)}
                  formatValue={(v) => v ? v.toLocaleString() : '-'}
                  highlight="highest"
                />
                <ComparisonRow
                  label="Growth"
                  values={deals.map(d => d.startup.traction.metrics.find(m => m.name === 'Growth')?.value ?? 0)}
                  formatValue={(v) => v ? `${v}%` : '-'}
                  highlight="highest"
                />
              </View>
            </Animated.View>

            {/* Match Reasons */}
            {matchScores.some(m => m?.reasons.length) && (
              <Animated.View
                entering={FadeIn.delay(600).duration(300)}
                className="mx-5 mt-4 rounded-2xl overflow-hidden"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <View
                  className="px-4 py-3 border-b"
                  style={{ borderColor: colors.border.light }}
                >
                  <Text style={{ color: colors.text.primary }} className="font-semibold">
                    Why They Match
                  </Text>
                </View>
                <View className="flex-row px-4 py-3">
                  <View className="w-28" />
                  {matchScores.map((match, index) => (
                    <View key={index} className="flex-1 px-2">
                      {match?.reasons.map((reason, rIndex) => (
                        <View key={rIndex} className="flex-row items-start gap-1 mb-1">
                          <Check size={12} color={colors.success} className="mt-0.5" />
                          <Text style={{ color: colors.text.secondary }} className="text-xs flex-1">
                            {reason}
                          </Text>
                        </View>
                      )) || (
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">
                          No match data
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              </Animated.View>
            )}

            {/* Action Buttons */}
            <Animated.View
              entering={FadeIn.delay(700).duration(300)}
              className="flex-row gap-3 mx-5 mt-6 mb-8"
            >
              {deals.map((deal) => (
                <AnimatedPressable
                  key={deal.id}
                  onPress={() => router.push(`/startup-detail?id=${deal.id}`)}
                  className="flex-1 py-3 rounded-xl items-center"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <Text className="text-white font-medium text-sm">
                    View {deal.startup.company.name.split(' ')[0]}
                  </Text>
                </AnimatedPressable>
              ))}
            </Animated.View>
          </ScrollView>
        </SafeAreaView>
      </View>
  );

  if (isWeb) {
    return (
      <AppLayout title="Compare Deals" breadcrumbs={BREADCRUMBS}>
        {content}
      </AppLayout>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      {content}
    </>
  );
}
